/**
 * Module-level live-feed buffer. Subscribes to slurpWs once at app boot
 * and keeps the last N trade/launch events in memory regardless of which
 * route the user is on. The LiveFeed component reads from here, so when
 * the user navigates dashboard → token page → back, the bar already has
 * the trades that fired while they were away.
 */

import { slurpWs } from "./slurpApi";

const API_BASE = process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";
const MAX_ENTRIES = 30;
const MIN_USD = 0.1;

let entries = [];
const subscribers = new Set();

const emit = () => {
  for (const fn of subscribers) {
    try { fn(entries); } catch (err) { console.error("[liveFeedStore] subscriber:", err); }
  }
};

const push = (entry) => {
  // Dedup by id — same trade signature can arrive twice if the indexer
  // re-broadcasts (logsSubscribe + safetyNet replay race) or if React
  // double-mounts a subscriber.
  if (entries.some((e) => e.id === entry.id)) return;
  // Newest at FRONT (leftmost in render). Marquee scrolls left→right so
  // the leftmost rendered position is the entry point.
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  emit();
};

// Seed from the API on module load so the bar isn't empty after a refresh.
// Failure here is non-fatal — the bar will just stay empty until a WS event
// comes in.
(async () => {
  try {
    const r = await fetch(`${API_BASE}/api/stats/recent-activity?limit=${MAX_ENTRIES}&min_usd=${MIN_USD}`);
    if (!r.ok) return;
    const { items } = await r.json();
    if (!Array.isArray(items)) return;
    const seeded = items
      .map((it) => {
        if (it.kind === "launch") {
          return {
            id: it.id,
            kind: "launch",
            wallet: it.wallet,
            mint: it.mint,
            symbol: it.symbol || "?",
            image_url: it.image_url || null,
            usd: 0,
            ts: +new Date(it.ts) || Date.now(),
          };
        }
        // Burn replay — amount_raw + decimals → whole tokens, matches
        // the live WS handler below so the chip renders identically
        // whether it arrived live or via seed.
        if (it.kind === "burn") {
          const decimals = Number(it.decimals) || 6;
          const whole = Number(it.amount_raw) / 10 ** decimals;
          return {
            id: it.id,
            kind: "burn",
            wallet: null,
            mint: it.mint,
            symbol: it.symbol || "?",
            image_url: it.image_url || null,
            amount_whole: whole,
            usd: 0,
            ts: +new Date(it.ts) || Date.now(),
          };
        }
        if (it.kind === "compound") {
          return {
            id: it.id,
            kind: "compound",
            wallet: null,
            mint: it.mint,
            symbol: it.symbol || "?",
            image_url: it.image_url || null,
            usd: Number(it.added_usd) || 0,
            ts: +new Date(it.ts) || Date.now(),
          };
        }
        const sol = Number(it.sol_raw) / 1e9;
        const solUsd = Number(it.sol_usd);
        const usd = sol * solUsd;
        if (!Number.isFinite(usd)) return null;
        return {
          id: it.id,
          kind: it.kind,
          wallet: it.wallet,
          mint: it.mint,
          symbol: it.symbol || "?",
          image_url: it.image_url || null,
          usd,
          ts: +new Date(it.ts) || Date.now(),
        };
      })
      .filter(Boolean);
    // Newest first (leftmost in render). Marquee scrolls left→right so
    // newest enters from the left edge.
    seeded.sort((a, b) => b.ts - a.ts);
    entries = seeded.slice(0, MAX_ENTRIES);
    emit();
  } catch (err) {
    console.warn("[liveFeedStore] seed failed:", err);
  }
})();

// Allow consumers to register a callback for profile-pre-attached events
// so they can warm their own caches before the first render. Used by the
// LiveFeed to seed its profile cache from `trader_profile` / `creator_profile`
// on the WS message — this is what makes new chips render with the
// username on the same frame they appear, instead of waiting on the
// batch /api/profiles/batch round-trip.
const profileSubscribers = new Set();
const emitProfile = (profile) => {
  if (!profile || !profile.wallet) return;
  for (const fn of profileSubscribers) {
    try { fn(profile); } catch (err) { console.error("[liveFeedStore] profile subscriber:", err); }
  }
};

if (slurpWs) {
  slurpWs.subscribe((msg) => {
    if (!msg || typeof msg !== "object") return;

    if (msg.type === "trade:new" && msg.trade && msg.token) {
      const sol = Number(msg.trade.sol_raw) / 1e9;
      const solUsd = Number(msg.trade.sol_usd);
      const usd = sol * solUsd;
      if (!Number.isFinite(usd) || usd < MIN_USD) return;
      if (msg.trader_profile) emitProfile(msg.trader_profile);
      push({
        id: msg.trade.signature,
        kind: msg.trade.side === "buy" ? "buy" : "sell",
        wallet: msg.trade.trader,
        mint: msg.token.mint,
        symbol: msg.token.symbol || "?",
        image_url: msg.token.image_url || null,
        usd,
        ts: Date.now(),
      });
      return;
    }

    if (msg.type === "token:new" && msg.token) {
      if (msg.creator_profile) emitProfile(msg.creator_profile);
      push({
        id: `launch:${msg.token.mint}`,
        kind: "launch",
        wallet: msg.token.creator,
        mint: msg.token.mint,
        symbol: msg.token.symbol || "?",
        image_url: msg.token.image_url || null,
        usd: 0,
        ts: Date.now(),
      });
      return;
    }

    // Burn worker fired: render a flame chip showing how much supply was
    // torched. amount_raw is in token units; we convert to whole here so
    // the chip can format compactly (e.g. "482K $LEZ burned").
    if (msg.type === "burn:executed" && msg.burn) {
      const b = msg.burn;
      const decimals = Number(b.decimals) || 6;
      const whole = Number(b.amount_raw) / 10 ** decimals;
      push({
        id: `burn:${b.signature}`,
        kind: "burn",
        wallet: null,
        mint: b.mint,
        symbol: b.symbol || "?",
        image_url: b.image_url || null,
        amount_whole: whole,
        usd: 0,
        ts: Date.now(),
      });
      return;
    }

    // lp_compound worker added liquidity from accrued fees. added_usd is
    // a pre-computed dollar value so the chip just formats it.
    if (msg.type === "compound:executed" && msg.compound) {
      const c = msg.compound;
      push({
        id: `compound:${c.signature}`,
        kind: "compound",
        wallet: null,
        mint: c.mint,
        symbol: c.symbol || "?",
        image_url: c.image_url || null,
        usd: Number(c.added_usd) || 0,
        ts: Date.now(),
      });
    }
  });
}

export const liveFeedStore = {
  getEntries: () => entries,
  subscribe: (fn) => {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },
  // Subscribe to profile snippets pre-attached to incoming WS events.
  // The LiveFeed wires this into its profileCache so brand-new chips
  // render the username with zero round-trip.
  subscribeProfiles: (fn) => {
    profileSubscribers.add(fn);
    return () => profileSubscribers.delete(fn);
  },
};
